/**
 * The render loop — SPEC.md §3, §12 (M1); issue #1 "Implementation Decisions".
 *
 * All timing, sequencing and state logic lives behind this one factory. It
 * does not import Mermaid, touch React, or reach for the DOM — the renderer
 * is injected, which is what makes the whole thing testable with no browser.
 */

/**
 * Calls the injected renderer with a per-attempt id and the current source.
 * `onNotice` (M4.5, issue #19) lets the renderer report a one-line,
 * mid-attempt status message — e.g. "Loading ZenUML plugin" the first time a
 * session hits a plugin diagram type — without this module knowing anything
 * about Mermaid or plugins. The loop clears it automatically at the start of
 * every new attempt and at the end of every completed one; the renderer only
 * ever sets it, never clears it itself.
 */
export type RenderFn = (id: string, source: string, onNotice: (notice: string) => void) => Promise<{ svg: string }>;

/** What the sheet is currently showing — exactly one of four at any moment. */
export type PreviewState = 'empty' | 'drawing' | 'drawn' | 'stale';

export interface RenderError {
  /** Best-effort. Extracted from the error's message text; absence is normal, not a bug. */
  line: number | null;
  /** Mermaid's own message, verbatim — never paraphrased. */
  message: string;
}

export interface LoopState {
  /** The SVG currently on the sheet: the fresh one, or the last good render dimmed. */
  state: PreviewState;
  svg: string | null;
  /** Present whenever the current source is invalid; null on success. */
  error: RenderError | null;
  /** Duration of the last completed successful render, in ms. */
  renderMs: number | null;
  /** A one-line, mid-attempt status message from the renderer; null the rest of the time. */
  notice: string | null;
}

export interface RenderLoop {
  setSource(source: string): void;
  forceRender(): void;
  subscribe(listener: (state: LoopState) => void): () => void;
  destroy(): void;
}

const INITIAL_STATE: LoopState = { state: 'empty', svg: null, error: null, renderMs: null, notice: null };

/**
 * Best-effort line extraction. Mermaid's jison-backed types put a clean
 * "Parse error on line N:" in the message; the Langium-backed `-beta` family
 * sometimes bundles several lexer/parser errors into one message with the
 * first line mentioned being the most useful anchor available. Either way
 * this is a defensive parse of already-displayed text, not a paraphrase of
 * it — and it must never throw.
 */
function extractLine(message: string): number | null {
  const match = /line (\d+)/i.exec(message);
  if (!match) return null;
  const line = Number(match[1]);
  return Number.isFinite(line) ? line : null;
}

function toRenderError(err: unknown): RenderError {
  const message = err instanceof Error ? err.message : String(err);
  return { line: extractLine(message), message };
}

export function createRenderLoop(deps: { render: RenderFn; debounceMs?: number }): RenderLoop {
  const { render, debounceMs = 300 } = deps;

  let source = '';
  let attempt = 0;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  let state: LoopState = INITIAL_STATE;

  const listeners = new Set<(state: LoopState) => void>();

  function emit(patch: Partial<LoopState>) {
    if (destroyed) return;
    state = { ...state, ...patch };
    for (const listener of listeners) listener(state);
  }

  function clearDebounce() {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
  }

  async function runRender() {
    const mine = ++attempt;
    emit({ state: 'drawing', notice: null });
    const startedAt = Date.now();

    function onNotice(notice: string) {
      if (destroyed || mine !== attempt) return;
      emit({ notice });
    }

    try {
      const result = await render(`plotter-render-${mine}`, source, onNotice);
      if (destroyed || mine !== attempt) return;
      emit({ state: 'drawn', svg: result.svg, error: null, renderMs: Date.now() - startedAt, notice: null });
    } catch (err) {
      if (destroyed || mine !== attempt) return;
      emit({ state: state.svg !== null ? 'stale' : 'empty', error: toRenderError(err), notice: null });
    }
  }

  return {
    setSource(next: string) {
      source = next;
      clearDebounce();
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        void runRender();
      }, debounceMs);
    },

    forceRender() {
      clearDebounce();
      void runRender();
    },

    subscribe(listener) {
      listeners.add(listener);
      listener(state);
      return () => listeners.delete(listener);
    },

    destroy() {
      destroyed = true;
      clearDebounce();
      listeners.clear();
    },
  };
}
